import React from "react";
import { REPORT_PAGES_ROUTES } from "@sscs/report/constants";
import { convertScanCheckLabel } from "@sscs/report/utils";
import { Card, CheckLabel, LongTextLabel, Section } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { FileStatus, LinkToReport } from "Components";
import { Version } from "Types";
import { useVersionReportSummary } from "../../../hooks";
import styles from "./checksCard.module.scss";
import DeleteReproBuild from "./DeleteReproBuild";
import {
  Check,
  CheckStatus,
  getAllChecks,
  getCheckStatus,
  getChecksStats,
  getChecksSummaryText,
} from "./helpers";
import UploadReproBuild from "./UploadReproBuild";

const REPRO_CHECK_COUNT = 3;

const CheckRow = ({
  version,
  label,
  processingStatus,
  description,
  checkType,
}: Check & { version: Version }): React.JSX.Element => {
  const convertedStatus = convertScanCheckLabel(label);
  // Repro and version need to be scanned with the same policy profile
  // It is safe to assume version and repro artifacts have the same levels
  const isLevelCustomized =
    version.artifacts_summary.version.latest_report_summary?.scan_report
      .inhibitors.customized ?? false;
  const isRepro = checkType.startsWith("REPRO");
  let goToPage: string | undefined = undefined;

  if (checkType === "VERSION-DIFF") {
    goToPage = REPORT_PAGES_ROUTES.VERSION_DIFF;
  } else if (checkType === "REPRO-DIFF" || checkType === "REPRO-CHECK") {
    goToPage = REPORT_PAGES_ROUTES.REPRODUCIBILITY_ROUTE;
  }

  const isDone = processingStatus?.code === "DONE";

  return (
    <div className={styles["check-row"]}>
      {isDone ? (
        <CheckLabel
          status={convertedStatus.status}
          scanLevel={convertedStatus.scanLevel}
          isDiffCheck={convertedStatus.isDiff}
          isLevelCustomized={isLevelCustomized}
          isReproducibilityCheck={convertedStatus.isRepro}
        />
      ) : (
        <FileStatus
          status={processingStatus}
          scanReport={undefined}
          scanDuration={undefined}
        />
      )}
      <div className={styles["check-name"]}>
        <LongTextLabel label={description} />
      </div>
      {isDone && (
        <LinkToReport
          id={`details-${description}`}
          report={{
            projectName: version.project_name,
            packageName: version.package_name,
            version: version.software_info.version,
            isRepro: isRepro,
          }}
          goToPage={goToPage}
        >
          Details
        </LinkToReport>
      )}
    </div>
  );
};

const SEGMENT_CLASS: Record<CheckStatus, string | undefined> = {
  pass: styles.pass,
  fail: styles.fail,
  pending: styles.pending,
  none: undefined,
};

const STATUS_RANK: Record<CheckStatus, number> = {
  pass: 0,
  fail: 1,
  pending: 2,
  none: 3,
};

type ChecksCardProps = { version: Version; className?: string };

const ChecksCard = ({
  version,
  className,
}: ChecksCardProps): React.JSX.Element => {
  const { hasReproBuild } = useVersionReportSummary(version);
  const checkList = getAllChecks(version);
  const stats = getChecksStats(checkList);
  const { title } = getChecksSummaryText(stats);

  const versionChecks = checkList.filter(
    (check) => !check.checkType.startsWith("REPRO"),
  );
  const reproChecks = checkList.filter((check) =>
    check.checkType.startsWith("REPRO"),
  );

  const details = [
    `${stats.passed} passed`,
    `${stats.failed} failed`,
    ...(stats.pending > 0 ? [`${stats.pending} pending`] : []),
  ];

  const barChecks = [...checkList].sort(
    (first, second) =>
      STATUS_RANK[getCheckStatus(first)] - STATUS_RANK[getCheckStatus(second)],
  );

  return (
    <Card.Card fullWidth className={className}>
      <Card.Title as="h2" details={details}>
        {title}
      </Card.Title>
      <Card.Content className={styles.content}>
        <div className={styles["status-bar"]}>
          {barChecks.map((check) => (
            <span
              key={check.description}
              className={getClassName(
                styles.segment,
                SEGMENT_CLASS[getCheckStatus(check)],
              )}
            />
          ))}
        </div>

        <Section>
          <Section.Title as="h3">Version</Section.Title>
          <Section.Content>
            {versionChecks.map((check) => (
              <CheckRow key={check.description} version={version} {...check} />
            ))}
          </Section.Content>
        </Section>

        <Section>
          <Section.Title as="h3">Reproducible build</Section.Title>
          {hasReproBuild && (
            <Section.Action className={styles["repro-action-slot"]}>
              <div className={styles["repro-action"]}>
                <span className={styles["repro-pill"]}>
                  <small className={styles["repro-tag"]}>REPRO</small>
                  <LongTextLabel
                    label={
                      version.artifacts_summary.repro?.file_name ??
                      "None uploaded"
                    }
                  />
                </span>
                <DeleteReproBuild
                  version={version}
                  className={styles["delete-repro"]}
                />
              </div>
            </Section.Action>
          )}
          <Section.Content>
            {hasReproBuild ? (
              reproChecks.map((check) => (
                <CheckRow
                  key={check.description}
                  version={version}
                  {...check}
                />
              ))
            ) : (
              <UploadReproBuild
                versionId={version.version_id}
                checkCount={REPRO_CHECK_COUNT}
              />
            )}
          </Section.Content>
        </Section>
      </Card.Content>
    </Card.Card>
  );
};

export default ChecksCard;
